import { FOCUS_RING_NO_RADIUS } from '@amelio/ui-library';

/**
 * Sizing and layout constants shared by the calibration-report page and its components. Tailwind
 * scale tokens and no hardcoded colors — with two documented exceptions, both
 * shared with the neighbouring report pages (`CompetencyMatrix/reportStyles.ts`,
 * `Compensation/compensationStyles.ts`):
 *
 * - a `text-[24px]` page title, the `text-2xl` step spelt out, kept as written so the three report
 *   pages stay titled by one shared string;
 * - `level.color`, the company-chosen hex the report DTO carries, applied as an inline style
 *   where each component reads it.
 *
 * Every responsive rule below states only its `max-*` half rather than the usual "base class +
 * `bp:` override" pair: `app.css` imports the library's compiled sheet after Tailwind and both
 * emit into `@layer utilities`, so a library-emitted bare utility (`w-full` is one) sits later in
 * the layer than our breakpoint override and silently wins at every width. Same reasoning, and
 * same remedy, as `REPORT_PROFILE_SELECT_WIDTH` in the competency-matrix report.
 */

/** Vertical rhythm of the page's own sections. */
export const PAGE_STACK = 'flex flex-col gap-6';

/** Page title: the same 24px as its two neighbours (`REPORT_TITLE`), bold rather than medium so
 * the report opens on a title with the weight the design gives it. */
export const CALIBRATION_TITLE = 'text-[24px] font-bold';

/** Header: title and subtitle on the left, the cycle selector alone on the right. Wraps to two
 * rows below `sm`, where a 320px selector and a title cannot share a line. */
export const HEADER_LAYOUT = 'flex flex-wrap items-end justify-between gap-4';

/** Cycle selector: full width below `sm`, wide enough for a full cycle title from `sm` up. */
export const CYCLE_SELECT_WIDTH = 'max-sm:w-full sm:w-[320px]';

export const VIEW_TILES_GRID = 'grid gap-4 max-sm:grid-cols-1 sm:grid-cols-2';

export const TILE_BASE = `${FOCUS_RING_NO_RADIUS} flex w-full items-start gap-4 rounded-2xl border p-6 text-left transition-colors`;
export const TILE_ACTIVE = 'border-primary bg-primary-50';
export const TILE_INACTIVE = 'border-base-100 bg-white hover:bg-base-25';
export const TILE_ICON_WRAPPER =
    'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10';

export const FILTER_BAR_LAYOUT = 'flex flex-wrap items-start gap-3';

/** Padded body of a report card — the tab strip sits flush against the card's edge, so the card
 * itself carries no padding and each section inside declares its own. */
export const CARD_SECTION = 'flex flex-col gap-6 p-6';

/** Explanatory paragraph above a chart: capped so a long sentence stays readable on a wide
 * screen rather than running the full card width. */
export const CHART_INTRO = 'max-w-3xl';

/** Legend strip closing a chart card: swatches first, cohort figures last, wrapping as one row. */
export const CHART_LEGEND_LAYOUT = 'flex flex-wrap items-center gap-x-6 gap-y-2';
export const CHART_LEGEND_ENTRY = 'inline-flex items-center gap-2';
export const CHART_LEGEND_SWATCH = 'h-4 w-4 shrink-0 rounded';

/** Differentiation view: the explanation on the left, the sort selector on the right. */
export const DIFFERENTIATION_HEADER_LAYOUT = 'flex flex-wrap items-start justify-between gap-4';
export const SORT_SELECT_WIDTH = 'max-sm:w-full sm:w-[220px]';

/** Draft banner opening the employee card: status on the left, the two bulk actions on the
 * right, on a lighter panel than the card it sits in. */
export const DRAFT_BANNER = 'rounded-xl bg-white/70 px-5 py-4';
export const DRAFT_BANNER_LAYOUT = 'flex flex-wrap items-center justify-between gap-4';
export const DRAFT_BANNER_STATUS = 'flex min-w-0 flex-1 flex-wrap items-center gap-3';
export const DRAFT_BANNER_ACTIONS = 'flex flex-wrap items-center gap-3';

/** Name search above the table, kept to the right so the table itself stays the focus. */
export const TABLE_TOOLBAR_LAYOUT = 'flex justify-end';

/** Name search above the table: full width below `sm`, a search field's width from `sm` up. */
export const TABLE_SEARCH_WIDTH = 'max-sm:w-full sm:w-[288px]';

export const TABLE_CONTAINER =
    'scrollbar-glass overflow-x-auto rounded-2xl border border-base-100 bg-white';

export const TABLE_HEADER_CELL = 'px-3 py-3 text-left align-middle';
export const TABLE_CELL = 'px-3 py-3 align-middle';
export const TABLE_SORT_BUTTON = `${FOCUS_RING_NO_RADIUS} inline-flex items-center gap-1.5 rounded-md`;

/**
 * Column widths, applied to the header cells so every row lines up and the seven columns fit a
 * ~1120px content area without a horizontal scroll — `TABLE_CONTAINER` keeps one for narrower
 * viewports. They are `min-w-*` rather than `w-*`: a `table-fixed` layout would clip the level
 * pills, while a plain `w-*` on an auto-layout table is only a hint the browser overrides as soon
 * as one cell's content is wider.
 */
export const TABLE_PERSON_COLUMN_WIDTH = 'min-w-[200px]';
export const TABLE_MANAGER_COLUMN_WIDTH = 'min-w-[120px]';
export const TABLE_SCORE_COLUMN_WIDTH = 'min-w-[96px]';
export const TABLE_LEVEL_COLUMN_WIDTH = 'min-w-[140px]';
export const TABLE_CALIBRATED_SCORE_COLUMN_WIDTH = 'min-w-[96px]';
export const TABLE_COMMENT_COLUMN_WIDTH = 'min-w-[160px]';

/** Calibrated-score field: wide enough for "100,0", narrow enough to read as a single figure. */
export const SCORE_INPUT_WIDTH = 'w-24';

export const MODAL_PANEL = 'w-full max-w-lg';
export const MODAL_STACK = 'flex flex-col gap-5 p-6';
export const MODAL_FIELD_STACK = 'flex flex-col gap-2';
export const MODAL_FOOTER = 'flex items-center justify-end gap-3';
export const MODAL_PREVIEW_PANEL = 'bg-base-25 flex flex-col gap-1 rounded-xl p-4';
